import * as React from 'react';
import Box from '@mui/material/Box';
import {useMediaQuery, useTheme} from '@mui/material';
import type {Module} from '../../types/modules';
import {componentMap} from '../../config/componentMap';
import DashboardPage from '../../pages/Dashboard/Dashboard';
import EmployeePage from '../../pages/Finance/Employee/EmployeePage';
import ContractsPage from '../../pages/Contracts/ContractsPage';
import PayrollPage from '../../pages/Finance/Payroll/PayrollPage';
import WorkEvidencePage from '../../pages/Finance/Payroll/WorkEvidencePage';
import MonthlyChangesPage from '../../pages/Finance/Payroll/MonthlyChangesPage';
import WorkEvidenceApprovalPage from '../../pages/Finance/Payroll/WorkEvidenceApprovalPage';
import ReportsPage from '../../pages/Reports/ReportsPage';
import JobPage from '../../pages/Hr/Jobs/JobPage';
import BranchesPage from '../../pages/Hr/Branches/BranchesPage';
import {MobilePeriodBranchFilterBar} from './MobilePeriodBranchFilterBar';

// Pages whose own mobile reference layout doesn't include the period/branch filter row
// (they have their own header/search/filter UI instead - see their mobile design references).
// Payroll's Periods tab already has its own period + year selector (PayrollPeriodMobile), so the
// global filter bar would just duplicate it. WorkEvidencePage, WorkEvidenceApprovalPage and
// ReportsPage render the bar themselves, below their own page title, instead of above it (see
// WorkEvidence.tsx / WorkEvidenceApproval.tsx / Reports.tsx).
const PAGES_WITHOUT_MOBILE_FILTER_BAR: ReadonlyArray<React.ComponentType<any>> = [
    DashboardPage,
    EmployeePage,
    ContractsPage,
    PayrollPage,
    WorkEvidencePage,
    MonthlyChangesPage,
    WorkEvidenceApprovalPage,
    ReportsPage,
    JobPage,
    BranchesPage,
];

interface NutoUiMainProps {
    readonly pathname: string;
    readonly modules: Module[];
    readonly loading: boolean;
    readonly error: string | null;
}

export function NutoUiMain({pathname, modules, loading, error}: Readonly<NutoUiMainProps>): React.ReactElement {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Decode URI component to handle spaces and special characters
    const decodedPath = decodeURIComponent(pathname);
    const segment = decodedPath.replace(/^\//, '') || 'Home';

    // The backend module tree can re-parent/flatten a module at any time, which changes the
    // full path Toolpad builds (e.g. "Human Resources/Employee" -> "Employee"). Prefer an exact
    // full-path match (for cases that intentionally differ by parent, e.g. aliasing), but fall
    // back to the leaf (last) segment so routing keeps working regardless of where a module sits
    // in the tree, without requiring a componentMap edit every time it's moved.
    const leafSegment = segment.split('/').pop() || segment;
    const Component = componentMap[segment] ?? componentMap[leafSegment];

    const renderComponent = () => {
        if (!Component) {
            console.warn(`No component found for segment: "${segment}" (leaf: "${leafSegment}", pathname: "${pathname}")`);
            return null;
        }

        // Pass modules, loading, error only to DashboardPage
        if (Component === DashboardPage) {
            return <Component modules={modules} loading={loading} error={error}/>;
        }
        return <Component/>;
    };

    return (
        <Box sx={{py: 4, px: 2.5, display: 'flex', flexDirection: 'column', textAlign: 'center'}}>
            {/* The dashboard renders the filter bar itself, below its welcome header; Employees and
                Contracts have their own header/search/filter UI (see their mobile design references)
                and don't show this bar either. */}
            {isMobile && !PAGES_WITHOUT_MOBILE_FILTER_BAR.includes(Component) && <MobilePeriodBranchFilterBar/>}
            {renderComponent()}
        </Box>
    );
}
